import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import { collection, addDoc, doc, updateDoc, getDocs, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useTranslation } from "react-i18next";
import CountryFlag from "react-country-flag";
import i18n from "./i18n";
import Select from 'react-select';


// Kroki formularza
const STEP = { DATE: 0, ORIGIN_COUNTRY: 1, GOODS: 2, DEST: 3, SUMMARY: 4, CONTACT: 5, SENT: 6 };

// Maksymalne wagi/objętości
const FCL_MAX_WEIGHT = { "20ft": 25000, "40ft": 28000 };
const LCL_MAX_DENSITY = 2000; // kg/m³

// Przełącznik języka
function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const langs = [
    { code: "pl", label: "PL" },
    { code: "en", label: "EN" },
    { code: "ua", label: "UA" },
    { code: "de", label: "DE" }
  ];
  return (
    <div className="space-x-3 select-none">
      {langs.map(({ code, label }) => (
        <a
          key={code}
          href="#"
          className={`text-blue-700 underline font-semibold cursor-pointer hover:text-blue-900 transition${i18n.language === code ? " opacity-70 pointer-events-none" : ""}`}
          onClick={e => {
            e.preventDefault();
            i18n.changeLanguage(code);
          }}
        >
          {label}
        </a>
      ))}
    </div>
  );
}

export default function Kalkulator() {
  const [step, setStep] = useState(STEP.DATE);
  const [transportType, setTransportType] = useState("LCL");
  const [date, setDate] = useState("");
  // Nowość: countryCode
  const [originCountry, setOriginCountry] = useState("");
  const [origin, setOrigin] = useState("");
  const [containerSize, setContainerSize] = useState("");
  const [lclRows, setLclRows] = useState([{ quantity: "", length: "", width: "", height: "" }]);
  const [lclTotalWeight, setLclTotalWeight] = useState("");
  const [fclWeight, setFclWeight] = useState("");
  const [destCountry, setDestCountry] = useState("PL");
  const [destination, setDestination] = useState("");
  const [comment, setComment] = useState("");
  const [accept, setAccept] = useState(false);
  const [email, setEmail] = useState("");
  const [offerId, setOfferId] = useState("");
  const [offerDocId, setOfferDocId] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [seaRows, setSeaRows] = useState([]);
  const [roadPricing, setRoadPricing] = useState([]);
  const [termsOpen, setTermsOpen] = useState(false);
  const [t1Value, setT1Value] = useState(65);
  const { t } = useTranslation();

  // SUMY LCL
  const totalQuantity = lclRows.reduce((s, r) => s + Number(r.quantity || 0), 0);
  const totalVolume = lclRows.reduce(
    (sum, row) => sum + ((row.quantity && row.length && row.width && row.height)
      ? (row.quantity * row.length * row.width * row.height) / 1000000 : 0), 0
  );

  // --- ŁADOWANIE DANYCH (cenniki, T1) ---
  useEffect(() => {
    setLoadingPricing(true);
    setPricingError(null);
    async function fetchPricing() {
      try {
        const seaSnap = await getDocs(collection(db, "seaPricing"));
        let arr = [];
        seaSnap.forEach(docSnap => arr.push({ id: docSnap.id, ...docSnap.data() }));
        setSeaRows(arr);

        const roadSnap = await getDocs(collection(db, "roadPricing"));
        let roadArr = [];
        roadSnap.forEach(docSnap => roadArr.push({ id: docSnap.id, ...docSnap.data() }));
        setRoadPricing(roadArr);

        // Ładuj T1 z settings
        const t1Ref = doc(db, "t1Settings", "currentT1");
        const t1Snap = await getDoc(t1Ref);
        let t1 = 65;
        if (t1Snap.exists() && typeof t1Snap.data().value === "number") {
          t1 = t1Snap.data().value;
        }
        setT1Value(t1);
      } catch (e) {
        setPricingError(e.message || t("Błąd ładowania cenników."));
        setSeaRows([]);
        setRoadPricing([]);
        setT1Value(65);
      }
      setLoadingPricing(false);
    }
    fetchPricing();
    // eslint-disable-next-line
  }, []);

  // ---- WYBÓR KRAJU ORIGIN (z cennika) ----
  const getAvailableOriginCountries = () => {
    if (!date) return [];
    // Tylko kraje, w których jest aktywny cennik dla wybranego typu transportu
    const allCodes = seaRows
      .filter(row =>
        row.active &&
        row.transportType === transportType &&
        (!row.dateFrom || !row.dateTo || (date >= row.dateFrom && date <= row.dateTo)) &&
        row.countryCode
      )
      .map(row => row.countryCode);
    return [...new Set(allCodes)];
  };

  // ---- WYBÓR PORTU (tylko z wybranego kraju) ----
  const getAvailablePorts = () => {
    if (!date || !originCountry) return [];
    return seaRows
      .filter(row =>
        row.active &&
        row.transportType === transportType &&
        row.countryCode === originCountry &&
        (!row.dateFrom || !row.dateTo || (date >= row.dateFrom && date <= row.dateTo))
      )
      .map(row => ({ port: row.port, countryCode: row.countryCode }))
      .filter((v, i, arr) => arr.findIndex(x => x.port === v.port) === i); // unikalne porty
  };

  // ---- WYBÓR miasta krajowego docelowego ----
  const getAvailableDestCountries = () => {
    // Z roadPricing wyciągnij wszystkie unikalne countryCode
    const allCodes = roadPricing
      .filter(r => r.active)
      .map(r => r.countryCode)
      .filter(Boolean);
    return [...new Set(allCodes)];
  };

  const getAvailableCities = () => {
    if (!destCountry) return [];
    let cities = [];
    if (transportType === "FCL") {
      cities = roadPricing
        .filter(r => r.active && r.mode === "FCL" && r.countryCode === destCountry && r.containerType === containerSize)
        .map(r => ({ city: r.city, countryCode: r.countryCode }))
        .filter((v, i, arr) => arr.findIndex(x => x.city === v.city) === i);
    } else {
      cities = roadPricing
        .filter(r =>
          r.active && r.mode === "LCL" && r.countryCode === destCountry &&
          Number(lclTotalWeight) >= Number(r.minWeight || 0) &&
          Number(lclTotalWeight) <= Number(r.maxWeight || 999999) &&
          totalVolume >= Number(r.minVolume || 0) &&
          totalVolume <= Number(r.maxVolume || 999999)
        )
        .map(r => ({ city: r.city, countryCode: r.countryCode }))
        .filter((v, i, arr) => arr.findIndex(x => x.city === v.city) === i);
    }
    // Zawsze dodajemy opcję custom, na końcu
    return [...cities, { city: "_CUSTOM_", countryCode: destCountry }];
  };
  // ---- Opcje dla Select z flagami krajów origin ----
const originCountryOptions = getAvailableOriginCountries().map(cc => ({
  value: cc,
  label: (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <CountryFlag countryCode={cc} svg style={{ width: 22, height: 22 }} />
      <span style={{ fontWeight: 600 }}>{cc}</span>
    </span>
  )
}));
// ---- Opcje dla Select z flagami krajów docelowych ----
  const countryOptions = getAvailableDestCountries().map(cc => ({
    value: cc,
    label: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CountryFlag countryCode={cc} svg style={{ width: 22, height: 22 }} />
        <span style={{ fontWeight: 600 }}>{cc}</span>
      </span>
    )
  }));
  
  // ---- REKORD cennika dla wybranego portu ----
  const getSeaRowRecord = () => {
    if (!origin) return null;
    if (transportType === "FCL") {
      return seaRows.find(
        r =>
          r.active &&
          r.transportType === "FCL" &&
          r.port === origin &&
          r.countryCode === originCountry &&
          r.containerType === containerSize &&
          (!r.dateFrom || !r.dateTo || (date >= r.dateFrom && date <= r.dateTo))
      );
    } else {
      return seaRows.find(
        r =>
          r.active &&
          r.transportType === "LCL" &&
          r.port === origin &&
          r.countryCode === originCountry &&
          (!r.dateFrom || !r.dateTo || (date >= r.dateFrom && date <= r.dateTo))
      );
    }
  };

  // ---- REKORD drogowy ----
  function getRoadPriceForCity(city) {
    if (!city) return 0;
    if (city === "Gdynia - port" || city === "Gdańsk - port") return 0;
    if (transportType === "FCL") {
      const rec = roadPricing.find(
        r => r.active && r.mode === "FCL" && r.city === city && r.countryCode === destCountry && r.containerType === containerSize
      );
      return rec ? Number(rec.roadPrice) : 0;
    } else {
      const rec = roadPricing.find(
        r =>
          r.active &&
          r.mode === "LCL" &&
          r.city === city &&
          r.countryCode === destCountry &&
          Number(lclTotalWeight) >= Number(r.minWeight || 0) &&
          Number(lclTotalWeight) <= Number(r.maxWeight || 999999) &&
          totalVolume >= Number(r.minVolume || 0) &&
          totalVolume <= Number(r.maxVolume || 999999)
      );
      return rec ? Number(rec.roadPrice) : 0;
    }
  }

  // ---- Kalkulacja cen ----
  const seaRow = getSeaRowRecord();
  let seaPrice = 0, thc = 0, adminFee = 0, minSeaPrice = 0;
  if (seaRow && transportType === "FCL") {
    seaPrice = Number(seaRow.price || 0);
    thc = Number(seaRow.thc || 0);
  }
  if (seaRow && transportType === "LCL") {
    const byWeight = Number(lclTotalWeight) * Number(seaRow.lclPricePerKg || 0);
    const byVolume = totalVolume * Number(seaRow.lclPricePerM3 || 0);
    minSeaPrice = Math.max(byWeight, byVolume, Number(seaRow.lclMinPrice || 0));
    seaPrice = minSeaPrice;
    adminFee = Number(seaRow.lclAdminFee || 0);
  }
  const roadPrice = getRoadPriceForCity(destination);

  // --- CENA: sumujemy (z T1) ---
  const totalPrice = (
    Number(seaPrice) +
    Number(thc) +
    Number(t1Value || 0) +
    Number(adminFee) +
    Number(roadPrice)
  ).toFixed(2);

  // --- GENEROWANIE OFFER ID ---
  function generateOfferId() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = String(now.getFullYear()).slice(2);
    const A = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    const B = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    return `${A}${B}${day}${month}${year}`;
  }

  // --- ZAPIS DO FIRESTORE NA KROKU PODSUMOWANIA ---
  useEffect(() => {
    if (step === STEP.SUMMARY) {
      const newOfferId = offerId || generateOfferId();
      setOfferId(newOfferId);
      const offerData = {
        date,
        transportType,
        originCountry,
        origin,
        containerSize,
        lclRows,
        lclTotalWeight,
        fclWeight,
        totalQuantity,
        totalVolume: totalVolume.toFixed(2),
        destCountry,
        destination,
        seaPrice,
        thc,
        t1: t1Value,
        adminFee,
        roadPrice,
        totalPrice,
        offerId: newOfferId,
        email,
        comment,
        userAgent: navigator.userAgent,
        createdAt: new Date().toISOString(),
        sent: false,
        rodoAccepted: accept,
      };
      // jeśli jeszcze nie było zapisu – dodaj, w przeciwnym wypadku uaktualnij
      if (!offerDocId) {
        addDoc(collection(db, "offers"), offerData)
          .then(docRef => setOfferDocId(docRef.id))
          .catch(() => {});
      } else {
        updateDoc(doc(db, "offers", offerDocId), offerData).catch(() => {});
      }
    }
    // eslint-disable-next-line
  }, [step]);

  // LCL zmiana wiersza
  const handleLclRowChange = (i, field, value) => {
    setLclRows(rows => rows.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };
  const handleAddLclRow = () => {
    if (lclRows.length < 20) setLclRows([...lclRows, { quantity: "", length: "", width: "", height: "" }]);
  };
  const handleRemoveLclRow = i => {
    if (lclRows.length > 1) setLclRows(rows => rows.filter((_, idx) => idx !== i));
  };

  // --- WYSYŁKA EMAILA + update e-mail w bazie ---
  const sendEmail = async (e) => {
    e.preventDefault();
    if (!email || !accept) {
      alert(t("Uzupełnij e-mail i zaznacz zgodę."));
      return;
    }
    if (comment.length > 300) {
      alert(t("Komentarz może mieć maksymalnie 300 znaków."));
      return;
    }
    const offerData = {
      date,
      transportType,
      originCountry,
      origin,
      containerSize,
      lclRows,
      lclTotalWeight,
      fclWeight,
      totalQuantity,
      totalVolume: totalVolume.toFixed(2),
      destCountry,
      destination,
      seaPrice,
      thc,
      t1: t1Value,
      adminFee,
      roadPrice,
      totalPrice,
      offerId,
      email,
      comment,
      userAgent: navigator.userAgent,
      createdAt: new Date().toISOString(),
      sent: true,
      rodoAccepted: accept,
    };
    // Update e-mail w bazie nawet jeśli mail nie wyjdzie
    if (offerDocId) {
      try {
        await updateDoc(doc(db, "offers", offerDocId), offerData);
      } catch (err) { }
    }
    emailjs.send(
      'service_u5x8nvv',
      'template_24qvp2j',
      offerData,
      'i4rAip2qJvT6bjv9n'
    ).then(
      () => setStep(STEP.SENT),
      () => setStep(STEP.SENT)
    );
  };

  // --- RENDER ---
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-[480px] h-[650px] p-8 flex flex-col overflow-y-auto">
        {/* Panel językowy – zawsze na samej górze panelu */}
        <div className="w-full flex justify-end mb-2">
          <LanguageSwitcher />
        </div>
        {/* Tytuł – zawsze pod językiem */}
        <h1 className="text-lg font-bold text-blue-700 mb-6 text-center">
          {t("Kalkulator Wyceny Transportu")}
        </h1>
        {pricingError && (
          <div className="text-red-500 font-bold mb-4">{pricingError}</div>
        )}

        {/* --- POCZĄTEK KODU POPUP --- */}
        {termsOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 relative flex flex-col" style={{ maxHeight: '90vh' }}>
              <h2 className="text-xl font-bold mb-4 text-blue-700 flex-shrink-0">{t("Regulamin i Polityka Prywatności")}</h2>
              <div className="overflow-y-auto mb-4 pr-2 text-sm text-gray-700">
                {/* ... (treść polityki prywatności jak poprzednio) ... */}
                <h3 className="font-bold mt-2 mb-1">I. {t("Postanowienia ogólne")}</h3>
                <p className="mb-2">
                  {t("Niniejszy regulamin określa zasady korzystania z kalkulatora wyceny transportu morskiego, dostępnego na stronie internetowej. Korzystanie z kalkulatora jest równoznaczne z akceptacją niniejszego regulaminu. Wycena ma charakter informacyjny i szacunkowy. Ostateczna cena transportu może się różnić i zostanie potwierdzona w oficjalnej ofercie przesłanej przez naszego pracownika.")}
                </p>
                <h3 className="font-bold mt-4 mb-1">II. {t("Polityka Prywatności (RODO)")}</h3>
                <p className="mb-2">
                  1. {t("Administratorem Danych Osobowych (ADO) jest Hyron Maryna Meleshko z siedzibą w Wołomin, ul. Wileńska 51, NIP5252461246.")}
                </p>
                <p className="mb-2">
                  2. {t("Twoje dane osobowe (adres e-mail) podane w formularzu będą przetwarzane wyłącznie w celu przedstawienia oferty handlowej dotyczącej wyceny transportu oraz kontaktu w tej sprawie, na podstawie Art. 6 ust. 1 lit. b RODO (przetwarzanie jest niezbędne do podjęcia działań na żądanie osoby, której dane dotyczą, przed zawarciem umowy).")}
                </p>
                <p className="mb-2">
                  3. {t("Podanie danych jest dobrowolne, ale niezbędne do otrzymania wyceny.")}
                </p>
                <p className="mb-2">
                  4. {t("Dane będą przechowywane przez okres niezbędny do realizacji procesu ofertowego, a następnie przez okres wymagany przepisami prawa lub do czasu wycofania zgody.")}
                </p>
                <p className="mb-2">
                  5. {t("Przysługuje Ci prawo dostępu do swoich danych, ich sprostowania, usunięcia, ograniczenia przetwarzania oraz prawo do wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.")}
                </p>
                <h3 className="font-bold mt-4 mb-1">III. {t("Zastrzeżenia")}</h3>
                <p>
                  {t("Kalkulator nie uwzględnia opłat dodatkowych, takich jak ubezpieczenie cargo, opłaty celne, podatki (VAT), koszty dodatkowych kontroli (np. sanitarnej, weterynaryjnej). Wszystkie te elementy zostaną uwzględnione w finalnej, spersonalizowanej ofercie.")}
                </p>
                <p>
                  {t("Kalkulator nie uwzględnia kosztów transportu wewnętrznego w Chinach (np. transportu z fabryki do portu załadunku).")}
                </p>
              </div>
              <div className="text-right flex-shrink-0 border-t pt-4">
                <button className="btn" onClick={() => setTermsOpen(false)}>
                  {t("Akceptuję i zamykam")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KROK 1: DATA */}
        {step === STEP.DATE && (
          <>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Data gotowości towaru")}</label>
              <input
                type="date"
                className="input"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>
            <div className="flex gap-4 mt-6">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!date}
                onClick={() => setStep(STEP.ORIGIN_COUNTRY)}
              >{t("Dalej")}</button>
            </div>
          </>
        )}

        {/* KROK 2: KRAJ ZAŁADUNKU I PORT ZAŁADUNKU */}
        {step === STEP.ORIGIN_COUNTRY && (
          <>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Rodzaj transportu")}</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" name="transport" value="LCL" checked={transportType === "LCL"} onChange={() => setTransportType("LCL")} />
                  <span className="ml-2">LCL</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="transport" value="FCL" checked={transportType === "FCL"} onChange={() => setTransportType("FCL")} />
                  <span className="ml-2">FCL</span>
                </label>
              </div>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Kraj załadunku")}</label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  name="originCountry"
                  options={originCountryOptions}
                  value={originCountryOptions.find(opt => opt.value === originCountry) || null}
                  onChange={opt => {
                    setOriginCountry(opt ? opt.value : "");
                    setOrigin(""); // resetuj port po zmianie kraju
                  }}
                  placeholder={t("-- Wybierz kraj --")}
                  // Dodaj te same style tutaj dla spójności
                  styles={{
                    option: (provided) => ({
                      ...provided,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }),
                  }}
                />

            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Port załadunku")}</label>
              <select className="input" value={origin} onChange={e => setOrigin(e.target.value)}>
                <option value="">{t("-- Wybierz port --")}</option>
                {getAvailablePorts().map(({ port, countryCode }) =>
                  <option key={port} value={port}>
                    {port}
                  </option>
                )}
              </select>
         
            </div>
            {transportType === "FCL" && (
              <div className="mb-4">
                <label className="block font-semibold mb-2">{t("Rozmiar kontenera")} </label>
                <select className="input" value={containerSize} onChange={e => setContainerSize(e.target.value)}>
                  <option value="">{t("-- Wybierz --")}</option>
                  {[...new Set(
                    seaRows.filter(r =>
                      r.active &&
                      r.transportType === "FCL" &&
                      r.countryCode === originCountry &&
                      r.port === origin &&
                      (!r.dateFrom || !r.dateTo || (date >= r.dateFrom && date <= r.dateTo))
                    ).map(r => r.containerType)
                  )].map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-4 mt-6">
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition flex-1" onClick={() => setStep(STEP.DATE)}>{t("Wstecz")}</button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!origin || (transportType === "FCL" && !containerSize)}
                onClick={() => setStep(STEP.GOODS)}
              >{t("Dalej")}</button>
            </div>
          
            </div>
                      </>
        )}

        

        {/* KROK 3: DANE ŁADUNKU */}
        {step === STEP.GOODS && (
          <>
            {transportType === "LCL" && (
              <>
                <div className="mb-2 font-semibold">{t("Wypełnij parametry wszystkich partii ładunku:")}</div>
                <div className="mb-4">
                  <table className="w-[85%] text-sm border mb-2 mx-auto">
                    <thead>
                      <tr className="bg-blue-50">
                        <th className="text-left px-1">{t("Ilość")}</th>
                        <th className="text-left px-1">{t("Dł. cm")}</th>
                        <th className="text-left px-1">{t("Szer. cm")}</th>
                        <th className="text-left px-1">{t("Wys. cm")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lclRows.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <input type="number" min={1} className="w-12 px-0.5 py-1 border border-gray-300 rounded text-xs" value={row.quantity} onChange={e => handleLclRowChange(i, "quantity", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" min={1} className="w-12 px-0.5 py-1 border border-gray-300 rounded text-xs" value={row.length} onChange={e => handleLclRowChange(i, "length", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" min={1} className="w-12 px-0.5 py-1 border border-gray-300 rounded text-xs" value={row.width} onChange={e => handleLclRowChange(i, "width", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" min={1} className="w-12 px-0.5 py-1 border border-gray-300 rounded text-xs" value={row.height} onChange={e => handleLclRowChange(i, "height", e.target.value)} />
                          </td>
                          <td>
                            {lclRows.length > 1 &&
                              <button className="text-red-500" onClick={() => handleRemoveLclRow(i)} title="Usuń">✖️</button>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition w-[50%] mx-auto"
                    onClick={handleAddLclRow}
                    disabled={lclRows.length >= 20}
                  >
                    {t("+ Dodaj ładunek")}
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block font-semibold mb-2">{t("Waga całkowita (kg)")}</label>
                  <input type="number" min={1} value={lclTotalWeight} onChange={e => setLclTotalWeight(e.target.value)} className="w-[25%] px-6 py-2 border border-gray-300 rounded bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 transition" />
                </div>
                <div className="mb-2 text-xs text-gray-600">
                  <b>{t("Suma ilości:")}</b> {totalQuantity}, <b>{t("Suma objętości:")}</b> {totalVolume.toFixed(2)} m³
                </div>
              </>
            )}
            {transportType === "FCL" && (
              <div className="mb-4">
                <label className="block font-semibold mb-2">{t("Waga całkowita (kg)")}:</label>
                <input type="number" min={1} value={fclWeight} onChange={e => setFclWeight(e.target.value)} className="w-[30%] px-3 py-2 border border-gray-300 rounded bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 transition" />
              </div>
            )}
            <div className="flex gap-4 mt-6">
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition flex-1" onClick={() => setStep(STEP.ORIGIN_COUNTRY)}>{t("Wstecz")}</button>
              {transportType === "FCL" && containerSize && Number(fclWeight) > FCL_MAX_WEIGHT[containerSize] && (
                <div className="text-red-600 text-sm font-bold mb-2">
                  {t("Przekroczono dopuszczalną ładowność - proszę o kontakt z działem obsługi klienta")}
                </div>
              )}
              {transportType === "LCL" && totalVolume > 0 && Number(lclTotalWeight) >= 1000 && Number(lclTotalWeight) / totalVolume > LCL_MAX_DENSITY && (
                <div className="text-red-600 text-sm font-bold mb-2">
                  {t("Ciężki ładunek - proszę o kontakt z działem obsługi klienta")}
                </div>
              )}
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  transportType === "LCL"
                    ? !lclRows.every(r => r.quantity && r.length && r.width && r.height)
                      || !lclTotalWeight
                      || (totalVolume > 0 && Number(lclTotalWeight) >= 1000 && Number(lclTotalWeight) / totalVolume > LCL_MAX_DENSITY)
                    : !fclWeight
                      || (containerSize && Number(fclWeight) > FCL_MAX_WEIGHT[containerSize])
                }
                onClick={() => setStep(STEP.DEST)}
              >{t("Dalej")}</button>
            </div>
          </>
        )}

        

        {/* KROK 4: WYBÓR MIASTA + KOMENTARZ + ZGODA */}
        {step === STEP.DEST && (
          <>
          <div className="mb-1">
  <label className="block font-semibold mb-2">{t("Kraj docelowy")}</label>
      <Select
      options={countryOptions}
      value={countryOptions.find(opt => opt.value === destCountry) || null}
      onChange={opt => {
        setDestCountry(opt ? opt.value : "");
        setDestination(""); // Zresetuj miasto docelowe również, gdy kraj jest czyszczony
      }}
      isClearable
      placeholder={t("-- Wybierz kraj --")}
      classNamePrefix="react-select"
      styles={{
        option: (provided) => ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }),
        singleValue: (provided) => ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }),
      }}
    />
    </div>
             
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Miasto docelowe")}</label>
              <select className="input" value={destination} onChange={e => setDestination(e.target.value)}>
                <option value="">{t("-- Wybierz miasto --")}</option>
                {getAvailableCities().map(({ city, countryCode }) =>
                  city === "_CUSTOM_"
                    ? <option key="custom" value="_CUSTOM_">{t("Twoje miasto (nie ma na liście)")}</option>
                    : <option key={city} value={city}>{city}</option>
                )}
              </select>
              <div className="text-xs text-gray-600 mt-2">
                {t("Jeśli nie ma Twojego miasta na liście – wybierz \"Twoje miasto\" i wpisz je poniżej.")}
              </div>
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Komentarz do oferty (opcjonalnie, max 300 znaków)")}</label>
              <textarea
                className="input w-full min-h-[70px] mb-1"
                maxLength={300}
                rows={4}
                value={comment}
                onChange={e => setComment(e.target.value.slice(0, 300))}
                placeholder={
                  destination === "_CUSTOM_"
                    ? t("Podaj miasto docelowe i ewentualnie inne uwagi…")
                    : t("Napisz dodatkowe pytania, lub podaj miasto docelowe jeśli nie ma go na liście...")
                }
                required={destination === "_CUSTOM_"}
              />
              <span className="text-xs text-gray-500">{comment.length}/300</span>
            </div>
            <div className="mb-2 mt-2">
              <label className="font-semibold mb-2 block text-sm">
                <input type="checkbox" className="mr-2 align-middle" checked={accept} onChange={e => setAccept(e.target.checked)} />
                {t("Akceptuję")} <button type="button" className="underline text-blue-600" onClick={() => setTermsOpen(true)}>{t("regulamin i politykę prywatności")}</button> {t("oraz wyrażam zgodę na przetwarzanie danych osobowych dla potrzeb wyceny.")}.
              </label>
            </div>
            <div className="flex gap-4 mt-2">
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition flex-1" onClick={() => setStep(STEP.GOODS)}>{t("Wstecz")}</button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!destination || !accept || (destination === "_CUSTOM_" && !comment)}
                onClick={() => setStep(STEP.SUMMARY)}
              >
                {t("Szybka wycena")}
              </button>
            </div>
          </>
        )}

        {/* KROK 5: PODSUMOWANIE */}
        {step === STEP.SUMMARY && (
          <>
            <h2 className="text-lg font-bold mb-2 text-blue-700">{t("Podsumowanie")}</h2>
            <ul className="mb-4 text-sm">
              <li>
                <b>{t("Port załadunku:")}</b>{" "}
                <CountryFlag countryCode={originCountry} svg style={{ width: "1.5em", height: "1.5em", verticalAlign: "middle" }} />{" "}
                {originCountry} {origin}
              </li>
              <li><b>{t("Rodzaj transportu:")}</b> {transportType}</li>
              {transportType === "FCL" && <li><b>{t("Rozmiar kontenera:")}</b> {containerSize}</li>}
              {transportType === "LCL" && (
                <>
                  <li><b>{t("Suma ilości:")}</b> {totalQuantity}</li>
                  <li><b>{t("Suma objętości:")}</b> {totalVolume.toFixed(2)} m³</li>
                  <li><b>{t("Waga całkowita:")}</b> {lclTotalWeight} kg</li>
                  <li><b>{t("Szczegóły partii ładunku:")}</b>
                    <ul className="ml-4 list-disc">
                      {lclRows.map((row, i) => (
                        <li key={i}>
                          {row.quantity} {t("szt.")} {row.length}x{row.width}x{row.height} cm
                        </li>
                      ))}
                    </ul>
                  </li>
                </>
              )}
              {transportType === "FCL" && (
                <li><b>{t("Waga całkowita:")}</b> {fclWeight} kg</li>
              )}
              <li>
                <b>{t("Miasto docelowe:")}</b>{" "}
                <CountryFlag countryCode={destCountry} svg style={{ width: "1.5em", height: "1.5em", verticalAlign: "middle" }} />{" "}
                {destCountry} {destination}
              </li>
              <li><b>{t("Data gotowości:")}</b> {date}</li>
              <li><b>{t("Numer oferty:")}</b> {offerId}</li>
              {comment && <li><b>{t("Komentarz:")}</b> {comment}</li>}
            </ul>
            <div className="mb-6 text-sm font-semibold text-green-700">
              <div className="mb-1">
                <div className="inline-flex items-center gap-1">
                  {t("Fracht morski:")} {seaPrice} USD
                  <span title={t("Fracht morski: wyliczony na podstawie aktualnego cennika dla wybranego portu i typu transportu.")} className="ml-1 text-blue-600 cursor-pointer">ℹ️</span>
                </div>
              </div>
              {thc > 0 && (
                <div className="mb-1">
                  <div className="inline-flex items-center gap-1">
                    THC: {thc} USD
                    <span title={t("Terminal Handling Charge (opłata portowa) doliczana dla kontenerów FCL.")} className="ml-1 text-blue-600 cursor-pointer">ℹ️</span>
                  </div>
                </div>
              )}
              {t1Value > 0 && (
                <div className="mb-1">
                  <div className="inline-flex items-center gap-1">
                    T1: {t1Value} USD
                    <span title={t("Tranzyt celny (T1) – koszt obsługi procedury celnej w imporcie.")} className="ml-1 text-blue-600 cursor-pointer">ℹ️</span>
                  </div>
                </div>
              )}
              {adminFee > 0 && (
                <div className="mb-1">
                  <div className="inline-flex items-center gap-1">
                    {t("Opłata administracyjna:")} {adminFee} USD
                    <span title={t("Opłata administracyjna dla wycen LCL (ładunki drobnicowe).")} className="ml-1 text-blue-600 cursor-pointer">ℹ️</span>
                  </div>
                </div>
              )}
              {roadPrice > 0 && (
                <div className="mb-1">
                  <div className="inline-flex items-center gap-1">
                    {t("Dostawa drogowa:")} {roadPrice} USD
                    <span title={t("Koszt dostawy drogowej z portu do wskazanego miasta.")} className="ml-1 text-blue-600 cursor-pointer">ℹ️</span>
                  </div>
                </div>
              )}
              <div className="underline font-bold text-base mt-2">
                {t("Łącznie")}: {totalPrice} USD
              </div>
            </div>
            <div className="flex gap-4">
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition flex-1" onClick={() => setStep(STEP.DEST)}>{t("Wstecz")}</button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1"
                onClick={() => setStep(STEP.CONTACT)}
              >{t("Wyślij zapytanie")}</button>
            </div>
          </>
        )}

        {/* KROK 6: KONTAKT I WYSYŁKA */}
        {step === STEP.CONTACT && (
          <form onSubmit={sendEmail}>
            <div className="mb-4">
              <label className="block font-semibold mb-2">{t("Twój e-mail")}</label>
              <input
                type="email"
                name="user_email"
                className="input bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-blue-200"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-4">
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition flex-1" type="button" onClick={() => setStep(STEP.SUMMARY)}>{t("Wstecz")}</button>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed" type="submit" disabled={!email || !accept}>
                {t("Wyślij zapytanie")}
              </button>
            </div>
          </form>
        )}

        {/* KROK 7: PODZIĘKOWANIE */}
        {step === STEP.SENT && (
          <div className="text-center space-y-4">
            <div className="text-3xl text-green-700 font-bold">{t("Dziękujemy!")}</div>
            <div className="text-lg">{t("Twoje zapytanie zostało wysłane.")}<br />{t("Skontaktujemy się z Tobą na podany adres e-mail.")}</div>
            <button className="btn mt-4" onClick={() => window.location.reload()}>{t("Nowa wycena")}</button>
          </div>
        )}
      </div>
      {/* Copyright na dole */}
      <div className="w-full mt-8 text-center text-xs text-gray-400 select-none">
        © {new Date().getFullYear()} {t("Hyron Sp. z o.o. Wszelkie prawa zastrzeżone. Narzędzie opracowane przez")} <a href="https://www.hyron.pl" className="underline text-blue-400 hover:text-blue-600" target="_blank" rel="noopener noreferrer">Hyron</a>
      </div>
      {/* Tailwind custom style */}
      <style>{`
      .input {
        @apply w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 transition;
      }
      .btn {
        @apply bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition;
      }
      `}</style>
    </div>
  );
}
